import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useGetNotificationByIdQuery,
  useMarkAsReadMutation,
  useDeleteNotificationMutation,
} from "../../../store/admin/notificationSlice";
import {
  formatNotificationTime,
  getNotificationCategory,
  getNotificationIcon,
  getPriorityColor,
} from "../../../utils/notificationTypes";
import FallbackImage from "../../../components/Common/ImageWrapper";
import { ConfirmationModal } from "../../../components";
import { useUserType } from "../../../hooks";

const NotificationDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const userType = useUserType();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Determine guard from user type
  const guard = userType?.toLowerCase().includes("member") ? "member" : "staff";

  const {
    data: notification,
    isLoading,
    error,
  } = useGetNotificationByIdQuery({ notificationId: id, guard });
  const [markAsRead] = useMarkAsReadMutation();
  const [deleteNotification] = useDeleteNotificationMutation();

  // Mark as read when component mounts
  useEffect(() => {
    if (notification && !notification.is_read) {
      markAsRead({ notificationId: notification.id, guard });
    }
  }, [notification, markAsRead, guard]);

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteNotification({ notificationId: id, guard });
      const basePath = guard === "member" ? "/member/notifications" : "/super_admin/notifications";
      navigate(basePath);
    } catch (error) {
      console.error("Error deleting notification:", error);
      setIsDeleting(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
  };

  // Commented out - all notifications go to detail page only
  // const handleGoToAction = () => {
  //   if (notification?.action_url) {
  //     navigate(notification.action_url);
  //   }
  // };

  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "400px" }}
      >
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error || !notification) {
    const basePath = guard === "member" ? "/member/notifications" : "/super_admin/notifications";
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="alert alert-danger">
              <h4>Notification Not Found</h4>
              <p>
                The notification you're looking for doesn't exist or has been
                deleted.
              </p>
              <button
                className="btn btn-primary"
                onClick={() => navigate(basePath)}
              >
                Back to Notifications
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const category = getNotificationCategory(notification.type);
  const icon = getNotificationIcon(notification.type);
  const priorityColor = getPriorityColor(notification.priority);
  const timeText = formatNotificationTime(notification.created_at);

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <button
              className="btn btn-outline-secondary"
              onClick={() => {
                const basePath = guard === "member" ? "/member/notifications" : "/super_admin/notifications";
                navigate(basePath);
              }}
            >
              ← Back to Notifications
            </button>
            <div className="d-flex gap-2">
              {/* Commented out - all notifications go to detail page only */}
              {/* {notification.action_url && (
                <button className="btn btn-primary" onClick={handleGoToAction}>
                  Go to Action
                </button>
              )} */}
              <button className="btn btn-outline-danger" onClick={handleDelete}>
                Delete
              </button>
            </div>
          </div>

          {/* Notification Card */}
          <div className="card">
            <div className="card-header">
              <div className="d-flex align-items-center">
                <FallbackImage
                  src={icon}
                  height="40"
                  width="40"
                  className="me-3"
                />
                <div>
                  <h4 className="mb-1">{notification.title}</h4>
                  <div className="d-flex align-items-center gap-3">
                    <span
                      className="badge"
                      style={{
                        backgroundColor: category.color,
                        color: "white",
                      }}
                    >
                      {category.name}
                    </span>
                    <span
                      className="badge"
                      style={{
                        backgroundColor: priorityColor,
                        color: "white",
                      }}
                    >
                      {notification.priority?.toUpperCase()}
                    </span>
                    <span className="text-muted">{timeText}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="card-body">
              <div className="mb-4">
                <h5>Message</h5>
                <p className="lead">
                  {typeof notification.message === 'string' 
                    ? notification.message 
                    : (notification.message?.message || "No message content")}
                </p>
              </div>

              {/* Metadata */}
              {notification.metadata && (
                <div className="mb-4">
                  <h5>Details</h5>
                  <div className="row">
                    {Object.entries(notification.metadata).map(
                      ([key, value]) => (
                        <div key={key} className="col-md-6 mb-2">
                          <strong>
                            {key.replace(/_/g, " ").toUpperCase()}:
                          </strong>{" "}
                          {value}
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}

              {/* Status Information */}
              <div className="row">
                <div className="col-md-6 mb-3">
                  <h5 style={{ fontSize: "18px", fontWeight: 600, color: "#383838", marginBottom: "12px" }}>
                    Status
                  </h5>
                  <div className="d-flex align-items-center">
                    <div
                      className={`badge rounded-pill me-2 ${
                        notification.is_read ? "bg-success" : "bg-warning"
                      }`}
                      style={{ padding: "6px 12px", fontSize: "12px", fontWeight: 600 }}
                    >
                      {notification.is_read ? "READ" : "UNREAD"}
                    </div>
                    {notification.read_at && (
                      <small style={{ fontSize: "12px", color: "#525252" }}>
                        Read on{" "}
                        {new Date(notification.read_at).toLocaleString()}
                      </small>
                    )}
                  </div>
                </div>

                <div className="col-md-6 mb-3">
                  <h5 style={{ fontSize: "18px", fontWeight: 600, color: "#383838", marginBottom: "12px" }}>
                    Target
                  </h5>
                  <div style={{ fontSize: "14px", color: "#45464e" }}>
                    <strong>Type:</strong>{" "}
                    {notification.target_type?.toUpperCase()}
                    {notification.branch_name && (
                      <div style={{ marginTop: "4px" }}>
                        <strong>Branch:</strong> {notification.branch_name}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Timestamps */}
              <div className="mt-4 pt-3 border-top">
                <div className="row">
                  <div className="col-md-6">
                    <small className="text-muted">
                      <strong>Created:</strong>{" "}
                      {new Date(notification.created_at).toLocaleString()}
                    </small>
                  </div>
                  <div className="col-md-6">
                    <small className="text-muted">
                      <strong>Updated:</strong>{" "}
                      {new Date(notification.updated_at).toLocaleString()}
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Notification"
        message="Are you sure you want to delete this notification? This action cannot be undone."
        confirmText="Yes, Delete"
        cancelText="Cancel"
        confirmButtonClass="bg-danger text-white"
        cancelButtonClass="bg-secondary text-white"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default NotificationDetails;
